import * as React from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import Card from 'components/Card/Card';
import Heading from 'components/text/Heading';
import Paragraph from 'components/text/Paragraph';
import View from 'components/View';
import { useAppContext } from 'contexts/app';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
  className?: string;
}

const FirstStepsSection: React.FC<Props> = ({ className }) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const itemRef = React.useRef<HTMLDivElement>(null);
  const [scrollLeft, setScrollLeft] = React.useState(false);
  const [scrollRight, setScrollRight] = React.useState(false);
  const { creditCard } = useAppContext();
  const { title, itemsCollection } = creditCard?.nextSteps || {};
  const { items } = itemsCollection || {};

  const onScroll = () => {
    if (scrollRef.current != null && itemRef.current != null) {
      const itemWidth = itemRef.current.offsetWidth + 16;
      setScrollRight(
        scrollRef.current.scrollWidth -
          scrollRef.current.scrollLeft -
          scrollRef.current.offsetWidth >=
          itemWidth / 2
      );
      setScrollLeft(scrollRef.current.scrollLeft >= itemWidth / 2);
    }
  };

  const handleScroll = (direction: 'left' | 'right') => () => {
    if (itemRef.current != null && scrollRef.current != null) {
      const itemWidth = itemRef.current.offsetWidth + 16;
      const currentIndex = scrollRef.current.scrollLeft / itemWidth;
      if (direction === 'left') {
        scrollRef.current.scrollTo({
          left: Math.max(0, (currentIndex - 1) * itemWidth),
          behavior: 'smooth'
        });
      } else {
        scrollRef.current.scrollTo({
          left: (currentIndex + 1) * itemWidth,
          behavior: 'smooth'
        });
      }
    }
  };

  React.useEffect(() => {
    onScroll();
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  if (!items || !items.length) return null;

  return (
    <div className={clsx('FirstStepsSection', className, 'w-screen relative')}>
      {title && (
        <View className="mb-4 text-center">
          <Heading variant="h2">{title}</Heading>
        </View>
      )}
      <div className="relative">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="gallery no-scrollbar overflow-scroll flex gap-4 snap-x"
        >
          {items.map((item, idx) => (
            <Card
              ref={idx === 0 ? itemRef : undefined}
              key={item.title}
              className="flex-1 basis-80 border-xl flex-shrink-0 snap-start p-10"
            >
              <div className="mb-6 rounded-full bg-primary-lightest w-16 h-16 flex items-center justify-center">
                <span className="text-3xl text-primary font-semibold">{idx + 1}</span>
              </div>
              <div className="flex-1 justify-end gap-2 flex flex-col">
                <Heading>{item.title}</Heading>
                <Paragraph color="subtle">{item.description}</Paragraph>
              </div>
            </Card>
          ))}
        </div>
        <button aria-label="scroll left" onClick={handleScroll('left')}>
          <div
            className={clsx(
              scrollLeft
                ? 'bg-neutral-900 opacity-30 dark:bg-neutral-50 visible'
                : 'opacity-0 hidden',
              'animate-in fade-in absolute z-10 left-3 top-0 bottom-0 my-auto h-12 w-12 rounded-full hover:bg-neutral-900  flex items-center justify-center text-white dark:hover:bg-neutral-50 dark:text-neutral-900 hover:opacity-70'
            )}
          >
            <ChevronLeft />
          </div>
        </button>
        <button aria-label="scroll right" onClick={handleScroll('right')}>
          <div
            className={clsx(
              scrollRight
                ? 'bg-neutral-900 opacity-30 dark:bg-neutral-50 visible'
                : 'opacity-0 hidden',
              'transition-all absolute z-10 right-3 top-0 bottom-0 my-auto h-12 w-12 rounded-full hover:bg-neutral-900 flex items-center justify-center text-white dark:hover:bg-neutral-50 dark:text-neutral-900 hover:opacity-70'
            )}
          >
            <ChevronRight />
          </div>
        </button>
      </div>
    </div>
  );
};

export default FirstStepsSection;
